import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Calendar } from "lucide-react";
import { Layout } from "@/components/Layout";
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const nameSchema = z.object({
    full_name: z
        .string()
        .trim()
        .min(1, "Required")
        .max(120, "Keep it under 120 characters"),
});

const passwordSchema = z
    .object({
        old_password: z.string().min(1, "Required"),
        new_password: z.string().min(8, "At least 8 characters"),
        confirm: z.string().min(1, "Required"),
    })
    .refine((d) => d.new_password === d.confirm, {
        message: "Passwords do not match",
        path: ["confirm"],
    });

const formatMemberSince = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
        });
    } catch {
        return "—";
    }
};

export default function AccountPage() {
    const { user, updateProfile, changePassword, logout } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle("Account");

    const handleLogout = () => {
        logout();
        toast.success("Signed out");
        navigate("/login", { replace: true });
    };

    return (
        <Layout>
            <div className="animate-fade-in-up space-y-6" data-testid="account-page">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground">Account</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Update your profile, change your password, or sign out.
                    </p>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    <div className="space-y-6">
                        <ProfileCard user={user} updateProfile={updateProfile} />
                        <SubscriptionCard user={user} />
                        <MetaCard user={user} />
                    </div>
                    <div className="space-y-6">
                        <PasswordCard changePassword={changePassword} />
                        <SignOutCard onLogout={handleLogout} />
                    </div>
                </div>
            </div>
        </Layout>
    );
}

const ProfileCard = ({ user, updateProfile }) => {
    const [submitting, setSubmitting] = useState(false);
    const {
        register, handleSubmit, reset, formState: { errors, isDirty },
    } = useForm({
        resolver: zodResolver(nameSchema),
        defaultValues: { full_name: user?.full_name || "" },
    });

    const onSubmit = async (values) => {
        setSubmitting(true);
        try {
            const updated = await updateProfile(values.full_name.trim());
            toast.success("Name updated");
            reset({ full_name: updated.full_name });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                (err?.message?.includes("Network") ? "Connection issue. Please try again." : "Could not update name");
            toast.error(typeof msg === "string" ? msg : "Could not update name");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="border-border/80 shadow-sm" data-testid="account-profile-card">
            <CardHeader>
                <CardTitle className="text-base">Profile</CardTitle>
                <CardDescription>This is the name shown to peers in the all-sessions feed.</CardDescription>
            </CardHeader>
            <CardContent>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-4"
                    data-testid="account-profile-form"
                >
                    <div className="space-y-2">
                        <Label htmlFor="full_name">Full name</Label>
                        <Input
                            id="full_name"
                            data-testid="account-name-input"
                            {...register("full_name")}
                        />
                        {errors.full_name && (
                            <p className="text-xs text-destructive">{errors.full_name.message}</p>
                        )}
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting || !isDirty}
                        className="bg-primary hover:bg-primary-hover"
                        data-testid="account-name-submit-button"
                    >
                        {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" />Saving…</>) : "Save name"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};

const MetaCard = ({ user }) => (
    <Card className="border-border/80 shadow-sm" data-testid="account-meta-card">
        <CardHeader>
            <CardTitle className="text-base">Account details</CardTitle>
            <CardDescription>Read-only for now.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-3" data-testid="account-meta-email">
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Email</p>
                    <p className="text-foreground">{user?.email || "—"}</p>
                </div>
            </div>
            <div className="flex items-center gap-3" data-testid="account-meta-member-since">
                <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Member since</p>
                    <p className="text-foreground">{formatMemberSince(user?.created_at)}</p>
                </div>
            </div>
        </CardContent>
    </Card>
);

const PasswordCard = ({ changePassword }) => {
    const [submitting, setSubmitting] = useState(false);
    const {
        register, handleSubmit, reset, formState: { errors },
    } = useForm({ resolver: zodResolver(passwordSchema) });

    const onSubmit = async (values) => {
        setSubmitting(true);
        try {
            await changePassword(values.old_password, values.new_password);
            toast.success("Password updated");
            reset();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not change password";
            toast.error(typeof msg === "string" ? msg : "Could not change password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="border-border/80 shadow-sm" data-testid="account-password-card">
            <CardHeader>
                <CardTitle className="text-base">Change password</CardTitle>
                <CardDescription>Pick something at least 8 characters long.</CardDescription>
            </CardHeader>
            <CardContent>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-4"
                    data-testid="change-password-form"
                >
                    <PasswordField
                        id="old_password"
                        label="Current password"
                        error={errors.old_password?.message}
                        testid="change-old-password-input"
                        {...register("old_password")}
                    />
                    <PasswordField
                        id="new_password"
                        label="New password"
                        error={errors.new_password?.message}
                        testid="change-new-password-input"
                        {...register("new_password")}
                    />
                    <PasswordField
                        id="confirm"
                        label="Confirm new password"
                        error={errors.confirm?.message}
                        testid="change-confirm-password-input"
                        {...register("confirm")}
                    />
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-primary hover:bg-primary-hover"
                        data-testid="change-password-submit-button"
                    >
                        {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" />Saving…</>) : "Save password"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};

const SignOutCard = ({ onLogout }) => (
    <Card className="border-border/80 shadow-sm" data-testid="account-signout-card">
        <CardHeader>
            <CardTitle className="text-base">Sign out</CardTitle>
            <CardDescription>Sign out of this device.</CardDescription>
        </CardHeader>
        <CardContent>
            <Button
                variant="outline"
                onClick={onLogout}
                data-testid="account-logout-button"
            >
                <LogOut className="h-4 w-4" />
                Log out
            </Button>
        </CardContent>
    </Card>
);

const PasswordField = React.forwardRef(({ id, label, error, testid, ...rest }, ref) => (
    <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} type="password" data-testid={testid} ref={ref} {...rest} />
        {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
));
PasswordField.displayName = "PasswordField";

// ---------------------------------------------------------------
// Phase 19 — Subscription card. Renders complimentary / paid / free states.
// ---------------------------------------------------------------
const SubscriptionCard = ({ user }) => {
    const sub = user?.subscription || {};
    const plan = sub.plan;
    const status = sub.status;
    const expires = sub.expires_at;
    const daysLeft = user?.expires_in_days;
    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                day: "numeric", month: "long", year: "numeric",
            });
        } catch { return iso; }
    };

    const isComplimentary = plan === "complimentary" && status === "active";
    const isPaid = plan === "annual" && status === "active";

    return (
        <Card data-testid="account-subscription-card">
            <CardHeader>
                <CardTitle className="text-lg">Subscription</CardTitle>
                <CardDescription>Your current Happy Lab access.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                    {isComplimentary && (
                        <span data-testid="account-plan-pill" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            Complimentary access
                        </span>
                    )}
                    {isPaid && (
                        <span data-testid="account-plan-pill" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            Annual · Active
                        </span>
                    )}
                    {!isComplimentary && !isPaid && (
                        <span data-testid="account-plan-pill" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                            Free · 2-session quota
                        </span>
                    )}
                </div>
                {isComplimentary && (
                    <>
                        <p className="text-sm text-foreground" data-testid="account-complimentary-detail">
                            3-month grant · expires {fmt(expires)} · <span className="font-medium">{daysLeft} days remaining</span>
                        </p>
                        <a
                            href="/subscribe"
                            className="block text-sm text-primary hover:underline"
                            data-testid="account-subscribe-link"
                        >
                            After your grant ends, the annual plan is ₹2,999/year. Subscribe in advance →
                        </a>
                    </>
                )}
                {isPaid && (
                    <p className="text-sm text-foreground">
                        Annual plan · renews on {fmt(expires)} · <span className="font-medium">{daysLeft} days remaining</span>
                    </p>
                )}
                {!isComplimentary && !isPaid && (
                    <a
                        href="/subscribe"
                        className="block text-sm text-primary hover:underline"
                        data-testid="account-subscribe-link"
                    >
                        Unlock unlimited sessions for ₹2,999/year →
                    </a>
                )}
            </CardContent>
        </Card>
    );
};

